import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Scale, Users, HeartHandshake, Calendar, Megaphone,
  Vote, ScrollText, BarChart3, Settings2, LogOut, Home, Menu, ClipboardList, FolderCog, PanelLeftClose, PanelLeftOpen, ShieldCheck, KeyRound, Wallet, Newspaper, Activity } from "lucide-react";
import Emblem from "./Emblem";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { useSidebarCollapsed } from "../hooks/useSidebarCollapsed";
import { PageTip, HelpButton } from "./Tips";
import { UpgradeBanner, VersionBadge } from "./About";
import { EmailPausedBanner } from "./EmailPausedBanner";
import { AvatarMenu } from "./AvatarMenu";

const ITEMS = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/reconciliation", icon: Scale, label: "Payment Confirmations", perm: "finance" },
  { to: "/admin/members", icon: Users, label: "Members & Vetting" },
  { to: "/admin/dues", icon: Settings2, label: "Dues & Levies", perm: "finance" },
  { to: "/admin/welfare", icon: HeartHandshake, label: "Welfare Review", perm: "welfare" },
  { to: "/admin/events", icon: Calendar, label: "Events", perm: "events" },
  { to: "/admin/meetings", icon: ClipboardList, label: "Meetings", perm: "events" },
  { to: "/admin/content", icon: FolderCog, label: "Content", perm: "content_manage" },
  { to: "/admin/communications", icon: Megaphone, label: "Communications", perm: "broadcast" },
  { to: "/admin/newsletter", icon: Newspaper, label: "Newsletter", perm: "newsletter" },
  { to: "/admin/elections", icon: Vote, label: "Elections", perm: "elections" },
  { to: "/admin/money", icon: Wallet, label: "Finance", perm: "finance" },
  { to: "/admin/finance", icon: BarChart3, label: "Finance & Reports", perm: "finance" },
  { to: "/admin/roles", icon: ShieldCheck, label: "Roles & Access", perm: "admin_level" },
  { to: "/portal/profile", icon: KeyRound, label: "My Profile & Password" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Log", perm: "admin_level" },
  { to: "/admin/cron", icon: Activity, label: "Scheduled Jobs", perm: "admin_level" },
];

export const visibleAdminItems = (permissions) => ITEMS.filter((it) => !it.perm || (permissions || []).includes(it.perm));

const btnCls = (mini) => `w-full gap-3 text-parchment/70 hover:bg-white/10 ${mini ? "justify-center px-0" : "justify-start"}`;

function SidebarNav({ mini, onNavigate }) {
  const { permissions } = useAuth();
  const items = visibleAdminItems(permissions);
  return (
    <nav className="mt-2 flex-1 space-y-1 overflow-y-auto sidebar-scroll min-h-0 pr-1" data-testid="admin-sidebar-nav">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} title={mini ? it.label : undefined}
          data-testid={`admin-nav-${it.label.toLowerCase().replace(/[ &]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg py-2.5 text-sm transition-colors ${mini ? "justify-center px-0" : "px-3"} ${
            isActive ? "bg-gold text-slate-950 font-semibold" : "text-parchment/75 hover:bg-white/10"}`}>
          <it.icon size={18} className="shrink-0" /> {!mini && it.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter({ mini }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="space-y-2 border-t border-white/10 pt-3">
      <Button onClick={() => nav("/portal")} variant="ghost" title="Member Portal" className={btnCls(mini)}><LayoutDashboard size={18} /> {!mini && "Member Portal"}</Button>
      <Button onClick={() => nav("/")} variant="ghost" title="Public Site" className={btnCls(mini)}><Home size={18} /> {!mini && "Public Site"}</Button>
      <Button onClick={async () => { await logout(); nav("/"); }} variant="ghost" title="Sign out" className={btnCls(mini)} data-testid="admin-logout"><LogOut size={18} /> {!mini && "Sign out"}</Button>
    </div>
  );
}

function Sidebar({ mini = false, onNavigate }) {
  return (
    <>
      <Link to="/" className={`flex items-center gap-3 py-4 ${mini ? "justify-center px-0" : "px-2"}`} title="Okuapemman Association of Massachusetts"><Emblem size={40} />
        {!mini && <div><div className="font-serif-display text-base font-bold leading-tight text-gold">Okuapemman Association of Massachusetts</div>
        <div className="eyebrow text-palm-light">Officer Console</div></div>}
      </Link>
      <SidebarNav mini={mini} onNavigate={onNavigate} />
      <SidebarFooter mini={mini} />
    </>
  );
}

function AdminHeader({ collapsed, toggle, onMenu }) {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-slate-950/90 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] backdrop-blur lg:px-8">
      <div className="flex items-center gap-1 lg:hidden">
        <button className="flex h-11 w-11 items-center justify-center rounded-lg text-parchment" aria-label="Open menu" onClick={onMenu} data-testid="admin-mobile-toggle"><Menu /></button>
        <Link to="/" className="flex h-11 w-11 items-center justify-center rounded-lg text-parchment/70" title="Public site" aria-label="Public site" data-testid="admin-mobile-home"><Home size={20} /></Link>
      </div>
      <button onClick={toggle} className="hidden rounded-lg p-2 text-parchment/60 transition-colors hover:bg-white/10 hover:text-gold lg:block"
        title={collapsed ? "Expand menu" : "Collapse menu"} aria-label={collapsed ? "Expand menu" : "Collapse menu"} data-testid="admin-sidebar-toggle">
        {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
      </button>
      <div className="flex items-center gap-3 text-parchment">
        <HelpButton dark />
        <div className="text-right"><p className="text-sm font-semibold">{user?.name}</p><p className="eyebrow text-gold">{user?.role}</p></div>
        <AvatarMenu dark area="admin" />
      </div>
    </header>
  );
}

function useHashScroll() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); clearInterval(t); }
      else if (++tries > 20) clearInterval(t);
    }, 150);
    return () => clearInterval(t);
  }, [hash, pathname]);
}

export default function AdminLayout() {
  useHashScroll();
  const [open, setOpen] = useState(false);
  const { collapsed, toggle } = useSidebarCollapsed();
  const close = () => setOpen(false);
  return (
    <div className="flex min-h-screen bg-slate-900">
      <aside className={`fixed hidden h-screen flex-col overflow-hidden bg-slate-950 transition-[width] duration-200 lg:flex ${collapsed ? "w-[72px] p-3" : "w-64 p-4"}`} data-testid="admin-sidebar" data-collapsed={collapsed}>
        <Sidebar mini={collapsed} onNavigate={close} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col overflow-hidden bg-slate-950 p-4 pt-[max(1rem,env(safe-area-inset-top))]"><Sidebar onNavigate={close} /></aside>
        </div>
      )}
      <div className={`min-w-0 flex-1 transition-[margin] duration-200 ${collapsed ? "lg:ml-[72px]" : "lg:ml-64"}`}>
        <AdminHeader collapsed={collapsed} toggle={toggle} onMenu={() => setOpen(true)} />
        <div className="min-h-screen bg-parchment p-4 lg:p-8"><EmailPausedBanner /><UpgradeBanner /><PageTip /><Outlet /></div>
        <div className="px-6 pb-3 text-right"><Link to="/admin/about" className="hover:underline" data-testid="admin-about-link"><VersionBadge dark={false} /></Link></div>
      </div>
    </div>
  );
}
